import { dayKey, dayStartOf, repeatStarts, shiftDays, type RepeatRule } from "@otrack/domain";

/**
 * Google Calendar's `recurrence` lines (RFC 5545 RRULE and EXDATE): reading them to expand a
 * series on the device, and writing them from an Event's Repeat. Anything a Repeat can say is
 * written back; richer rules (yearly, "second Tuesday") are expanded but stay as Google has them.
 */

export interface RRule {
  freq: "daily" | "weekly" | "monthly" | "yearly";
  interval: number;
  /** Weekly: JavaScript weekdays. Monthly by weekday: one entry with its position. */
  byDay: Array<{ weekday: number; nth: number | null }>;
  byMonthDay: number | null;
  until: number | null;
  count: number | null;
  /** Parts a Repeat has no words for (BYSETPOS, BYMONTH, …), so the rule can't be edited here. */
  extra: boolean;
}

export interface Recurrence {
  rule: RRule | null;
  exdates: number[];
  /** The rule as a Repeat, or null when it says more than a Repeat can ("Custom, set in Google"). */
  repeat: RepeatRule | null;
}

const WEEKDAYS: Record<string, number> = { SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6 };
const WEEKDAY_CODES = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
const KNOWN_PARTS = new Set(["FREQ", "INTERVAL", "BYDAY", "BYMONTHDAY", "UNTIL", "COUNT", "WKST"]);

interface Property { name: string; params: Record<string, string>; value: string }

function parseLine(line: string): Property | null {
  // The value starts at the first colon outside a quoted parameter.
  let quoted = false;
  let split = -1;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === '"') quoted = !quoted;
    else if (char === ":" && !quoted) { split = index; break; }
  }
  if (split < 0) return null;
  const [name = "", ...rawParams] = line.slice(0, split).split(";");
  const params: Record<string, string> = {};
  for (const param of rawParams) {
    const eq = param.indexOf("=");
    if (eq > 0) params[param.slice(0, eq).toUpperCase()] = param.slice(eq + 1).replace(/^"|"$/g, "");
  }
  return { name: name.toUpperCase(), params, value: line.slice(split + 1) };
}

/** Offset of a time zone from UTC at an instant, in ms. */
function zoneOffset(instant: number, zone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: zone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }).formatToParts(instant);
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  return Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second")) - Math.floor(instant / 1000) * 1000;
}

/** The instant a wall-clock time in `zone` refers to. */
function zonedInstant(year: number, month: number, day: number, hour: number, minute: number, second: number, zone: string): number {
  const guess = Date.UTC(year, month, day, hour, minute, second);
  const first = guess - zoneOffset(guess, zone);
  return guess - zoneOffset(first, zone);
}

function validZone(zone: string | undefined): string | null {
  if (!zone) return null;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return zone;
  } catch {
    return null;
  }
}

/** Reads a DATE or DATE-TIME value. Dates come back as local midnight. */
function parseDate(value: string, params: Record<string, string>): number | null {
  const match = /^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})?(Z)?)?$/.exec(value.trim());
  if (!match) return null;
  const [, y, mo, d, h, mi, s, utc] = match;
  const year = Number(y), month = Number(mo) - 1, day = Number(d);
  if (h === undefined || params["VALUE"] === "DATE") return new Date(year, month, day).getTime();
  const hour = Number(h), minute = Number(mi), second = Number(s ?? 0);
  if (utc) return Date.UTC(year, month, day, hour, minute, second);
  const zone = validZone(params["TZID"]);
  return zone ? zonedInstant(year, month, day, hour, minute, second, zone) : new Date(year, month, day, hour, minute, second).getTime();
}

export function parseRRule(value: string): RRule | null {
  const parts = Object.fromEntries(value.split(";").filter(Boolean).map((part) => part.split("=") as [string, string]).map(([key, val]) => [key.toUpperCase(), val ?? ""]));
  const freq = { DAILY: "daily", WEEKLY: "weekly", MONTHLY: "monthly", YEARLY: "yearly" }[parts["FREQ"] ?? ""] as RRule["freq"] | undefined;
  if (!freq) return null;
  const byDay = (parts["BYDAY"] ?? "").split(",").filter(Boolean).flatMap((token) => {
    const match = /^([+-]?\d{1,2})?(SU|MO|TU|WE|TH|FR|SA)$/.exec(token.toUpperCase());
    return match ? [{ weekday: WEEKDAYS[match[2]!]!, nth: match[1] ? Number(match[1]) : null }] : [];
  });
  const monthDays = (parts["BYMONTHDAY"] ?? "").split(",").filter(Boolean);
  return {
    freq,
    interval: Math.max(1, Number(parts["INTERVAL"] ?? 1) || 1),
    byDay,
    byMonthDay: monthDays.length ? Number(monthDays[0]) || null : null,
    until: parts["UNTIL"] ? parseDate(parts["UNTIL"], {}) : null,
    count: parts["COUNT"] ? Number(parts["COUNT"]) || null : null,
    extra: Object.keys(parts).some((key) => !KNOWN_PARTS.has(key)) || monthDays.length > 1
  };
}

/** The rule as a Repeat, when a Repeat can say all of it. */
function asRepeat(rule: RRule, start: number): RepeatRule | null {
  if (rule.extra || rule.freq === "yearly" || rule.interval > 99 || (rule.count !== null && rule.count > 999)) return null;
  if (rule.byDay.some((day) => day.nth !== null)) return null;
  if (rule.freq === "monthly" && (rule.byDay.length || (rule.byMonthDay !== null && rule.byMonthDay !== new Date(start).getDate()))) return null;
  if (rule.freq === "daily" && (rule.byDay.length || rule.byMonthDay !== null)) return null;
  if (rule.freq === "weekly" && rule.byMonthDay !== null) return null;
  return {
    freq: rule.freq,
    interval: rule.interval,
    weekdays: rule.freq === "weekly" ? [...new Set(rule.byDay.map((day) => day.weekday))] : [],
    until: rule.until !== null ? dayKey(rule.until) : null,
    count: rule.count
  };
}

/** Reads a Google event's `recurrence`: its first RRULE and every EXDATE. RDATE makes it custom. */
export function parseRecurrence(lines: readonly string[] | undefined, start: number): Recurrence {
  let rule: RRule | null = null;
  let custom = false;
  const exdates: number[] = [];
  for (const line of lines ?? []) {
    const property = parseLine(line);
    if (!property) continue;
    if (property.name === "RRULE") {
      if (rule) custom = true;
      else rule = parseRRule(property.value);
    } else if (property.name === "EXDATE") {
      for (const value of property.value.split(",")) {
        const at = parseDate(value, property.params);
        if (at !== null) exdates.push(at);
      }
    } else if (property.name === "RDATE") custom = true;
  }
  return { rule, exdates, repeat: rule && !custom ? asRepeat(rule, start) : null };
}

/** Day of the month of the `nth` given weekday (negative counts from the end), or null. */
function nthWeekday(year: number, month: number, weekday: number, nth: number): number | null {
  const days = new Date(year, month + 1, 0).getDate();
  const matches: number[] = [];
  for (let day = 1; day <= days; day += 1) if (new Date(year, month, day).getDay() === weekday) matches.push(day);
  return (nth > 0 ? matches[nth - 1] : matches[matches.length + nth]) ?? null;
}

/** Original starts of a repeating series that starts at `start`, up to `to`. */
export function ruleStarts(start: number, rule: RRule, to: number): number[] {
  // UNTIL is an instant: whole days are generated, then anything starting after it is dropped.
  const until = rule.until;
  const starts = generate(start, rule, to);
  return until === null ? starts : starts.filter((at) => at <= until);
}

function generate(start: number, rule: RRule, to: number): number[] {
  const untilDay = rule.until !== null ? dayKey(rule.until) : null;
  const monthlyByWeekday = (rule.freq === "monthly" || rule.freq === "yearly") && rule.byDay.length > 0 && rule.byDay[0]!.nth !== null;
  if (!monthlyByWeekday) {
    const repeat: RepeatRule = rule.freq === "yearly"
      ? { freq: "monthly", interval: rule.interval * 12, weekdays: [], until: untilDay, count: rule.count }
      : { freq: rule.freq, interval: rule.interval, weekdays: rule.freq === "weekly" ? rule.byDay.map((day) => day.weekday) : [], until: untilDay, count: rule.count };
    // A BYMONTHDAY that differs from the start day moves the first occurrence there.
    if (rule.freq === "monthly" && rule.byMonthDay !== null && rule.byMonthDay > 0 && new Date(start).getDate() !== rule.byMonthDay) {
      const origin = new Date(start);
      const first = new Date(origin.getFullYear(), origin.getMonth() + (origin.getDate() > rule.byMonthDay ? 1 : 0), rule.byMonthDay, origin.getHours(), origin.getMinutes()).getTime();
      return repeatStarts(first, repeat, to);
    }
    return repeatStarts(start, repeat, to);
  }
  const { weekday, nth } = rule.byDay[0] as { weekday: number; nth: number };
  const origin = new Date(start);
  const until = untilDay ? shiftDays(dayStartOf(untilDay), 1) : Infinity;
  const starts: number[] = [];
  const step = rule.freq === "yearly" ? 12 * rule.interval : rule.interval;
  for (let index = 0; index < 2400; index += 1) {
    const month = new Date(origin.getFullYear(), origin.getMonth() + index * step, 1);
    const day = nthWeekday(month.getFullYear(), month.getMonth(), weekday, nth);
    if (day === null) continue;
    const at = new Date(month.getFullYear(), month.getMonth(), day, origin.getHours(), origin.getMinutes(), origin.getSeconds()).getTime();
    if (at < start) continue;
    if (at >= to || at >= until || (rule.count !== null && starts.length >= rule.count)) break;
    starts.push(at);
  }
  return starts;
}

const pad = (value: number, length = 2) => String(value).padStart(length, "0");

/** "20261023T080000Z": an instant in RFC 5545's UTC form, as Google uses in UNTIL and instance ids. */
export function utcBasic(ts: number): string {
  const date = new Date(ts);
  return `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`;
}

/** "20261023": a local day in RFC 5545's DATE form. */
export function dateBasic(ts: number): string {
  return dayKey(ts).replace(/-/g, "");
}

/**
 * The RRULE line for a Repeat. UNTIL is the end of its last day: a date for all-day series, the
 * instant that day ends for timed ones, as RFC 5545 requires.
 */
export function rruleLine(repeat: RepeatRule, start: number, allDay: boolean): string {
  const parts = [`FREQ=${repeat.freq.toUpperCase()}`];
  if (repeat.interval > 1) parts.push(`INTERVAL=${repeat.interval}`);
  if (repeat.freq === "weekly") {
    const weekdays = repeat.weekdays.length ? [...new Set(repeat.weekdays)] : [new Date(start).getDay()];
    parts.push(`BYDAY=${weekdays.sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7)).map((day) => WEEKDAY_CODES[day]).join(",")}`);
  }
  if (repeat.until) parts.push(`UNTIL=${allDay ? repeat.until.replace(/-/g, "") : utcBasic(shiftDays(dayStartOf(repeat.until), 1) - 1000)}`);
  else if (repeat.count !== null) parts.push(`COUNT=${repeat.count}`);
  return `RRULE:${parts.join(";")}`;
}

/** Replaces the RRULE in `lines` with `rule` (or removes it), keeping EXDATEs and the rest. */
export function withRule(lines: readonly string[] | undefined, rule: string | null): string[] {
  const rest = (lines ?? []).filter((line) => !/^RRULE[:;]/i.test(line));
  return rule ? [rule, ...rest] : [];
}

/**
 * The same series ended before `before`: COUNT becomes the number of occurrences already
 * generated, otherwise UNTIL moves to just before it. Used to split a series at an occurrence.
 */
export function endRuleBefore(line: string, start: number, before: number, allDay: boolean): string {
  const rule = parseRRule(line.replace(/^RRULE:/i, ""));
  if (!rule) return line;
  const kept = line.replace(/^RRULE:/i, "").split(";").filter((part) => !/^(UNTIL|COUNT)=/i.test(part));
  if (rule.count !== null) return `RRULE:${[...kept, `COUNT=${Math.max(1, ruleStarts(start, rule, before).length)}`].join(";")}`;
  return `RRULE:${[...kept, `UNTIL=${allDay ? dateBasic(shiftDays(before, -1)) : utcBasic(before - 1000)}`].join(";")}`;
}

/** The rest of a split series from occurrence `at` on: COUNT shrinks by the occurrences before it. */
export function ruleFrom(line: string, start: number, at: number): string {
  const rule = parseRRule(line.replace(/^RRULE:/i, ""));
  if (!rule || rule.count === null) return line;
  const kept = line.replace(/^RRULE:/i, "").split(";").filter((part) => !/^COUNT=/i.test(part));
  return `RRULE:${[...kept, `COUNT=${Math.max(1, rule.count - ruleStarts(start, rule, at).length)}`].join(";")}`;
}
